import React, { useCallback, useEffect, useState } from 'react';

import { Box, Flex, Portal } from '@chakra-ui/react';
import '@draft-js-plugins/mention/lib/plugin.css';
import { useDebounce } from 'components/hooks/useDebounce';
import { calculateDisplayEmail } from 'utils/helpers/netchexHelpers';

import { Avatar } from 'uiToolkitV1/Avatar';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { useMentionEmployeeQuery } from './hooks/dataHooks';

export const MentionComponent = ({ mention, className, ...props }) => {
  const { employee, name, metadata } = mention ?? {};
  const { profilePhotoSrc, id } = employee ?? {};

  return (
    <Flex
      alignItems="center"
      bg="#F2F2F2"
      borderRadius="4px"
      boxShadow="0px 0px 1px rgba(0, 0, 0, 0.25)"
      p="12x"
      display="inline-flex"
      key={id}
    >
      <Avatar size="16px" name={name} src={profilePhotoSrc} />

      <TextBodyMedium ml="8px">{name}</TextBodyMedium>
    </Flex>
  );
};

export const EntryComponent = ({ ...props }) => {
  const { mention } = props;
  const { name, employee, companyId, companyName } = mention ?? {};
  const { profilePhotoSrc, id } = employee ?? {};
  const displayEmail = calculateDisplayEmail(employee?.metadata ?? {});

  return (
    <Flex {...props} key={id} alignItems="center" height="60px" p="12px">
      <Avatar name={name} src={profilePhotoSrc} />
      <Flex ml="8px" flexDir="column">
        <TextBodyMedium ml="2px">{name}</TextBodyMedium>

        <TextLabel overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
          {companyId && companyName ? `${displayEmail} (${companyId}, ${companyName})` : displayEmail}
        </TextLabel>
      </Flex>
    </Flex>
  );
};

const MentionSuggestions = ({
  DraftJSMentionSuggestions,
  widgetType,
  usePortalForSuggestions,
  portalParentRef,
  exclusions = [],
  module,
  ...props
}) => {
  //   mention plugin states
  const [mentionOpen, setMentionOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 150);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (portalParentRef.current) {
      const rect = portalParentRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }
  }, [portalParentRef]);

  const { data = [], isLoading } = useMentionEmployeeQuery({
    value: debouncedSearchTerm,
    widgetType,
    limit: 30,
    module,
  });

  const suggestions = data.filter((s) => !exclusions.includes(s.employee?.id));

  const onOpenChange = useCallback((open) => {
    if (!open) {
      setSearchTerm('');
    }
    setMentionOpen(open);
  }, []);
  const onSearchChange = useCallback(({ value }) => {
    setSearchTerm(value);
  }, []);

  // if (isLoading) {
  //   return null;
  // }

  if (usePortalForSuggestions) {
    return (
      <Portal>
        {/*  rendering the mention suggestions in a portal at document.body level to avoid z-index issues but still keeping its original position */}
        <Box position="absolute" top={`${position.top}px`} left={`${position.left}px`} zIndex="popover">
          <DraftJSMentionSuggestions
            open={mentionOpen}
            onOpenChange={onOpenChange}
            onSearchChange={onSearchChange}
            suggestions={suggestions}
            entryComponent={EntryComponent}
            {...props}
          />
        </Box>{' '}
      </Portal>
    );
  }

  return (
    <DraftJSMentionSuggestions
      open={mentionOpen}
      onOpenChange={onOpenChange}
      onSearchChange={onSearchChange}
      suggestions={suggestions}
      entryComponent={EntryComponent}
      {...props}
    />
  );
};

export { MentionSuggestions };
